'use client';

import { useEffect, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type {
  ExportsXmpMetadata,
  V4CommentsPanelExportsXmpMetadata,
} from '@frameio/graphql-client/generated';
import { useGetActiveSequenceMetadataQuery } from '@frameio/shared/integrations/gql';
import {
  type ExportDialogConfig,
  type ExportDialogState,
  useExportDialogState,
} from '../../shared/hooks/useExportDialogState';
import {
  useGetShareForV4CommentsPanelExportsLazyQuery,
  useHydrateSequenceExportsAssetsQuery,
  useSequenceEventsSubscription,
} from '../gql';
import { ExportProgressProvider } from './ExportProgressContext/ExportProgressContext';

export type ExportItem = {
  projectId: string;
  assetId: string;
  shareId: string;
  shareUrl: string;
};

export type { ExportDialogConfig };

type SequenceExportContextValue = ExportDialogState & {
  sequenceExports: Array<ExportsXmpMetadata>;
  lastSequenceExport: ExportsXmpMetadata | null;
  isLoadingSequenceMetadata: boolean;
};

const SequenceExportContext = createContext<SequenceExportContextValue>({
  open: () => undefined,
  close: () => undefined,
  isOpen: false,
  config: null,
  sequenceExports: [],
  lastSequenceExport: null,
  isLoadingSequenceMetadata: false,
});

const HydrateSequenceExportsAssets = ({
  accountId,
  assetIds,
}: {
  accountId: string;
  assetIds: string[];
}) => {
  useHydrateSequenceExportsAssetsQuery({
    variables: { accountId, assetIds, first: assetIds.length },
  });

  return null;
};

export const SequenceExportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { open, close, isOpen, config } = useExportDialogState();

  const {
    data: activeSequenceMetadata,
    loading: isLoadingSequenceMetadata,
    refetch,
  } = useGetActiveSequenceMetadataQuery({
    context: {
      resolveWithUXP: true,
    },
  });

  useSequenceEventsSubscription({
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.sequenceEvents;
      const evtType = event?.__typename;

      if (
        evtType === 'SequenceActivatedPayload' ||
        evtType === 'SequenceClosedPayload'
      ) {
        refetch();
      }
    },
    context: {
      resolveWithUXP: true,
    },
  });

  const sequenceMetadata = useMemo(() => {
    return (
      activeSequenceMetadata?.activeProject?.activeSequence?.metadata ?? []
    );
  }, [activeSequenceMetadata]);

  const exportedAssetIdsByAccount = useMemo(() => {
    const assetsByAccount = new Map<string, string[]>();

    sequenceMetadata.forEach((item) => {
      if (
        item?.__typename !== 'SequenceExportsXMPMetadata' ||
        !item.accountId ||
        !item.assetId
      ) {
        return;
      }

      const assetIds = assetsByAccount.get(item.accountId) ?? [];
      assetIds.push(item.assetId);
      assetsByAccount.set(item.accountId, assetIds);
    });

    return Array.from(assetsByAccount);
  }, [sequenceMetadata]);

  const v4CommentsPanelShareIds = useMemo(() => {
    return sequenceMetadata
      .filter(
        (item) => item?.__typename === 'V4CommentsPanelExportsXMPMetadata'
      )
      .map((item) => (item as V4CommentsPanelExportsXmpMetadata).shareId);
  }, [sequenceMetadata]);

  // @todo (FCI-214): Replace with a hydrateShares query once available
  const [getShareForV4CommentsPanelExport] =
    useGetShareForV4CommentsPanelExportsLazyQuery();
  useEffect(() => {
    if (
      sequenceMetadata[0]?.__typename !== 'V4CommentsPanelExportsXMPMetadata'
    ) {
      return;
    }
    if (sequenceMetadata[0]?.shareId) {
      getShareForV4CommentsPanelExport({
        variables: {
          shareId: sequenceMetadata[0]?.shareId ?? '',
        },
      });
    }
    if (v4CommentsPanelShareIds.length > 0) {
      v4CommentsPanelShareIds.forEach((shareId) => {
        getShareForV4CommentsPanelExport({
          variables: {
            shareId: shareId ?? '',
          },
        });
      });
    }
  }, [
    getShareForV4CommentsPanelExport,
    sequenceMetadata,
    v4CommentsPanelShareIds,
  ]);

  const value = useMemo(() => {
    const filteredSequenceMetadata = sequenceMetadata.filter(
      (item): item is ExportsXmpMetadata => item !== null
    );
    return {
      open,
      close,
      isOpen,
      config,
      sequenceExports: filteredSequenceMetadata,
      lastSequenceExport: filteredSequenceMetadata[0] ?? null,
      isLoadingSequenceMetadata,
    };
  }, [
    sequenceMetadata,
    open,
    close,
    isOpen,
    config,
    isLoadingSequenceMetadata,
  ]);

  return (
    <SequenceExportContext.Provider value={value}>
      {exportedAssetIdsByAccount.map(([accountId, assetIds]) => (
        <HydrateSequenceExportsAssets
          key={accountId}
          accountId={accountId}
          assetIds={assetIds}
        />
      ))}
      <ExportProgressProvider>{children}</ExportProgressProvider>
    </SequenceExportContext.Provider>
  );
};

export const useOpenSequenceExportDialog = () =>
  useContextSelector(SequenceExportContext, (context) => context.open);

export const useCloseSequenceExportDialog = () =>
  useContextSelector(SequenceExportContext, (context) => context.close);

export const useSequenceExportDialogState = () =>
  useContextSelector(SequenceExportContext, (context) => ({
    isOpen: context.isOpen,
    close: context.close,
  }));

export const useSequenceExports = () =>
  useContextSelector(
    SequenceExportContext,
    (context) => context.sequenceExports
  );

export const useLastSequenceExport = () =>
  useContextSelector(
    SequenceExportContext,
    (context) => context.lastSequenceExport
  );

export const useIsLoadingSequenceMetadata = () =>
  useContextSelector(
    SequenceExportContext,
    (context) => context.isLoadingSequenceMetadata
  );

export const useSequenceExportDialogConfig = () =>
  useContextSelector(SequenceExportContext, (context) => context.config);
